import { SearchIcon } from '@chakra-ui/icons';
import { Box, Divider, Flex } from '@chakra-ui/layout';
import {
  Checkbox,
  CheckboxGroup,
  Input,
  InputGroup,
  InputRightElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';

import { Button, ButtonGroup } from '@revelio/ui';

import modalStyles from '../../components/Modal/themedModal.module.css';
import type { SecondaryFilterConfig } from '../../components/PageConfigProvider/page-reducer';
import { ALL_FILTER_DEFINITIONS } from '../engine/filterRegistry';
import { FilterId, FilterType } from '../types/filters';

/**
 * Default filters available for custom pages.
 * These are the standard filters that users typically need.
 * Excludes screener-specific filters (MULTI_*, company detail filters, etc.)
 */
export const DEFAULT_AVAILABLE_FILTER_IDS: FilterId[] = [
  // Primary entity filters
  FilterId.COMPANY,
  FilterId.GEOGRAPHY,
  FilterId.ROLE,
  // Dimension filters
  FilterId.SENIORITY,
  FilterId.SKILL,
  FilterId.CUSTOM_SKILL,
  FilterId.EDUCATION,
  FilterId.ETHNICITY,
  FilterId.GENDER,
  FilterId.TITLE,
  // Time filters
  FilterId.DATE_RANGE,
  FilterId.SNAPSHOT_DATE,
  // Data adjustment filters
  FilterId.CONTINGENT_WORKERS,
  FilterId.DATA_ADJUSTMENTS,
  // Sentiment filters
  FilterId.OVERALL_RATING,
  // Postings filters
  FilterId.POSTINGS_SOURCE,
  FilterId.METRIC_MODE,
  FilterId.KEYWORDS,
];

/**
 * Internal filters that should never be shown in the filter selection UI.
 */
const INTERNAL_FILTER_IDS: FilterId[] = [
  FilterId.ROLE_TAXONOMY,
  FilterId.SKILL_TAXONOMY,
];

interface FilterOption {
  id: FilterId;
  name: string;
}

/**
 * Get all available filter options for the selection modal.
 * Filters out internal filters and MULTI filters (which are screener-specific).
 */
const getAvailableFilterOptions = (): FilterOption[] => {
  return Object.entries(ALL_FILTER_DEFINITIONS)
    .filter(([id, def]) => {
      const filterId = id as FilterId;
      // Exclude internal filters
      if (INTERNAL_FILTER_IDS.includes(filterId)) return false;
      // Exclude MULTI filters (screener/TD specific)
      if (def.type === FilterType.MULTI) return false;
      // Exclude GROUP filters (they're containers, not actual filters)
      if (def.type === FilterType.GROUP) return false;
      return true;
    })
    .map(([id, def]) => ({
      id: id as FilterId,
      name: def.name,
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
};

interface FilterSelectModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentFilters: SecondaryFilterConfig[];
  onSave: (filters: SecondaryFilterConfig[]) => void;
}

export const FilterSelectModal = ({
  isOpen,
  onClose,
  currentFilters,
  onSave,
}: FilterSelectModalProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFilterIds, setSelectedFilterIds] = useState<FilterId[]>([]);

  const allFilterOptions = getAvailableFilterOptions();

  // Initialize temporary selection only when modal opens (not on every render).
  // Intentionally omit currentFilters so we don't reset user's edits when parent re-renders.
  // biome-ignore lint/correctness/useExhaustiveDependencies: migrated from develop
  useEffect(() => {
    if (!isOpen) return;
    const currentIds = currentFilters.map((f) => f.id);
    const nextIds =
      currentIds.length === 0
        ? DEFAULT_AVAILABLE_FILTER_IDS.filter((id) =>
            allFilterOptions.some((opt) => opt.id === id)
          )
        : currentIds;
    setSelectedFilterIds(nextIds);
    setSearchTerm('');
  }, [isOpen]);

  const filteredOptions = allFilterOptions.filter((option) =>
    option.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleSelectionChange = (values: (string | number)[]) => {
    // Maintain the order based on allFilterOptions
    const sortedValues = allFilterOptions
      .filter((opt) => values.includes(opt.id))
      .map((opt) => opt.id);
    setSelectedFilterIds(sortedValues);
  };

  const handleSave = () => {
    const filters: SecondaryFilterConfig[] = selectedFilterIds.map((id) => ({
      id,
    }));
    onSave(filters);
    onClose();
  };

  const handleSelectAll = () => {
    setSelectedFilterIds(allFilterOptions.map((opt) => opt.id));
  };

  const handleSelectNone = () => {
    setSelectedFilterIds([]);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      isCentered
      returnFocusOnClose={false}
      size="lg"
    >
      <ModalOverlay />
      <ModalContent className={modalStyles.content}>
        <ModalHeader color="text.primary">Manage Filters</ModalHeader>
        <ModalCloseButton />
        <Divider orientation="horizontal" />
        <ModalBody>
          <InputGroup>
            <Input
              placeholder="Search filters..."
              value={searchTerm}
              onChange={handleSearchChange}
            />
            <InputRightElement>
              <SearchIcon w={3.5} h={3.5} className={modalStyles.muted} />
            </InputRightElement>
          </InputGroup>

          <Flex justifyContent="flex-end" gap={2} mt={2} mb={2}>
            <Button
              variant="link"
              size="xs"
              color="lightBlue"
              onClick={handleSelectAll}
            >
              Select All
            </Button>
            <Button
              variant="link"
              size="xs"
              color="lightBlue"
              onClick={handleSelectNone}
            >
              Select None
            </Button>
          </Flex>

          <Box
            className={modalStyles.scrollArea}
            overflowY="scroll"
            maxH="350px"
            mt={2}
            pl={1}
          >
            <CheckboxGroup
              colorScheme="green"
              value={selectedFilterIds}
              onChange={handleSelectionChange}
            >
              <Flex flexDirection="column">
                {filteredOptions.map((option) => (
                  <Checkbox
                    key={option.id}
                    value={option.id}
                    color="text.primary"
                    data-testid={`${option.id}-checkbox`}
                    mb={2}
                  >
                    {option.name}
                  </Checkbox>
                ))}
              </Flex>
            </CheckboxGroup>
          </Box>
        </ModalBody>
        <ModalFooter>
          <ButtonGroup style={{ gap: '1rem' }}>
            <Button
              variant="link"
              style={{ fontSize: '12px' }}
              color="default"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              style={{ fontSize: '12px' }}
              color="primary"
              size="sm"
              onClick={handleSave}
              data-testid="save-filters-button"
            >
              Save
            </Button>
          </ButtonGroup>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
